// Leaders Bonus (Alex, 2026-09-28): pay-period KPI targets and the
// dashboard that scores a warehouse against them. Mirrors
// app/admin/controllers/leadersBonus.controller.js.
import { apiFetch } from "./apiFetch";

/** One rung of a points ladder: reaching `from` earns `points`. */
export interface LeadersBonusLadderStep { from: number; points: number; }
/** on_time steps are rate thresholds (%); the item steps are % of the period target. */
export type LeadersBonusMetric = "on_time" | "booked_in" | "put_away";
export type LeadersBonusLadders = Record<LeadersBonusMetric, LeadersBonusLadderStep[]>;

export const LEADERS_BONUS_METRICS: LeadersBonusMetric[] = ["on_time", "booked_in", "put_away"];

const ITEM_LADDER: LeadersBonusLadderStep[] = [
    { from: 80, points: 2 }, { from: 85, points: 4 }, { from: 90, points: 6 }, { from: 95, points: 8 }, { from: 100, points: 10 },
];
/** The scheme Alex set on 2026-09-28 — mirrors DEFAULT_LADDERS on the server. */
export const DEFAULT_LEADERS_BONUS_LADDERS: LeadersBonusLadders = {
    on_time: [
        { from: 98, points: 2 }, { from: 98.5, points: 4 }, { from: 99, points: 6 }, { from: 99.5, points: 8 }, { from: 100, points: 10 },
    ],
    booked_in: ITEM_LADDER,
    put_away: ITEM_LADDER,
};

/** One pay period for one warehouse, with its derived targets. */
export interface LeadersBonusPeriod {
    id: number;
    warehouse: string;
    /** YYYY-MM-DD, inclusive. */
    starts_on: string;
    ends_on: string;
    on_time_target_pct: number;
    /** Entered per WORKING day (Mon–Fri minus UK bank holidays). */
    booked_in_daily_target: number;
    put_away_daily_target: number;
    working_days: number;
    /** Working days through today inclusive (0 before the period starts). */
    working_days_elapsed: number;
    /** daily × working_days — the figure the dashboard shows. */
    booked_in_target: number;
    put_away_target: number;
    /** daily × working_days_elapsed — where the count "should" be today. */
    booked_in_expected_to_date: number;
    put_away_expected_to_date: number;
    /** Working days scored so far with today prorated by the clock. */
    pace_days?: number;
    today_working?: boolean;
    /** Working days from today (inclusive if a working day) to the period end. */
    working_days_remaining?: number;
    points_ladders: LeadersBonusLadders;
    updated_at: string | null;
}

export interface LeadersBonusPeriodInput {
    warehouse: string;
    starts_on: string;
    ends_on: string;
    on_time_target_pct: number;
    booked_in_daily_target: number;
    put_away_daily_target: number;
    /** Omitted = server defaults on create, unchanged on update. */
    points_ladders?: LeadersBonusLadders;
}

/** One KPI's score on its ladder. `value` = on-time rate % or % of period target (null = nothing to score). */
export interface LeadersBonusScore {
    value: number | null;
    points: number;
    max: number;
    next: LeadersBonusLadderStep | null;
    ladder: LeadersBonusLadderStep[];
}
export interface LeadersBonusPoints {
    total: number;
    max: number;
    on_time: LeadersBonusScore;
    booked_in: LeadersBonusScore;
    put_away: LeadersBonusScore;
}

/** early = of on_time, shipped on a day before the due day (counted on the day they shipped). */
export interface LeadersBonusOnTimeDay { date: string; total: number; on_time: number; early: number; working: boolean; }
export interface LeadersBonusFailDay { date: string; total: number; on_time: number; late: number; pct: number; }
export interface LeadersBonusBookedDay {
    date: string; working: boolean; units: number;
    hot: number; cold: number; measure: number; red: number; big_sheet: number;
}
export interface LeadersBonusPutAwayDay {
    date: string; working: boolean; units: number; hot: number; cold: number; totes: number;
}

export interface LeadersBonusDashboard {
    warehouse: string;
    today: string;
    /** Every period of the warehouse (for the picker), newest first. */
    periods: LeadersBonusPeriod[];
    /** null when the warehouse has no periods — the cards are absent then. */
    period: LeadersBonusPeriod | null;
    dispatch_cutoff?: string;
    points?: LeadersBonusPoints;
    on_time?: {
        target_pct: number;
        rate_pct: number | null;
        on_time: number;
        total: number;
        early: number;
        scored_days: number;
        fail_days: LeadersBonusFailDay[];
        daily: LeadersBonusOnTimeDay[];
    };
    booked_in?: {
        units: number; daily_target: number; target: number; expected_to_date: number;
        daily: LeadersBonusBookedDay[];
    };
    put_away?: {
        units: number; daily_target: number; target: number; expected_to_date: number;
        daily: LeadersBonusPutAwayDay[];
    };
    snapshot_at?: string;
}

const json = { "Content-Type": "application/json" };

const okOrThrow = async (res: Response) => {
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
};

/** Every period, or one warehouse's. */
export const fetchLeadersBonusPeriods = async (warehouse?: string): Promise<{ periods: LeadersBonusPeriod[]; today: string }> => {
    const qs = warehouse ? `?warehouse=${encodeURIComponent(warehouse)}` : "";
    return okOrThrow(await apiFetch(`/admin/leaders-bonus/periods${qs}`, {}));
};

/** Thrown by create/update when the dates clash with another period. */
export class LeadersBonusOverlapError extends Error {
    conflict: { id: number; starts_on: string; ends_on: string };
    constructor(conflict: { id: number; starts_on: string; ends_on: string }) {
        super("overlap");
        this.conflict = conflict;
    }
}

const saveOrThrow = async (res: Response): Promise<LeadersBonusPeriod> => {
    if (res.status === 409) {
        const body = await res.json().catch(() => ({}));
        throw new LeadersBonusOverlapError(body.conflict ?? { id: 0, starts_on: "", ends_on: "" });
    }
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return (await res.json()).period;
};

export const createLeadersBonusPeriod = async (input: LeadersBonusPeriodInput): Promise<LeadersBonusPeriod> =>
    saveOrThrow(await apiFetch(`/admin/leaders-bonus/periods`, { method: "POST", headers: json, body: JSON.stringify(input) }));

export const updateLeadersBonusPeriod = async (id: number, input: LeadersBonusPeriodInput): Promise<LeadersBonusPeriod> =>
    saveOrThrow(await apiFetch(`/admin/leaders-bonus/periods/${id}`, { method: "PUT", headers: json, body: JSON.stringify(input) }));

export const deleteLeadersBonusPeriod = async (id: number): Promise<void> => {
    const res = await apiFetch(`/admin/leaders-bonus/periods/${id}`, { method: "DELETE" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
};

/** The four cards for one warehouse; `periodId` omitted → the current period. */
export const fetchLeadersBonusDashboard = async (warehouse: string, periodId?: number | null): Promise<LeadersBonusDashboard> => {
    const params = new URLSearchParams({ warehouse });
    if (periodId) params.set("period_id", String(periodId));
    return okOrThrow(await apiFetch(`/admin/leaders-bonus/dashboard?${params.toString()}`, {}));
};
